import { Download, Loader2, TrendingUpDown } from 'lucide-react';
import React, { useState } from 'react';
import { Button } from 'src/components/ui/button';
import { Skeleton } from 'src/components/ui/skeleton';
import { RawDataPoint, CampaignMetrics, LineItemMetrics, CreativeMetrics } from 'src/redux/admin/reporting/reporting.types';

interface MetricBreakdownTableProps {
  data: RawDataPoint[];
  isLoading: boolean;
  shouldShowMetric: (metric: string) => boolean;
  handleDownload: () => void | Promise<void>;
}

const formatNumber = (num: number, decimals: boolean = false): string => {
  if (!isFinite(num) || isNaN(num)) return 'N/A';
  if (decimals) {
    return num.toFixed(2).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }
  return num.toFixed(0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const formatCurrency = (value: number): string => {
  if (!isFinite(value) || isNaN(value)) return 'N/A';
  return `$${formatNumber(value, true)}`;
};

const formatPercentage = (value: number): string => {
  if (!isFinite(value) || isNaN(value)) return 'N/A';
  return `${formatNumber(value * 100, true)}%`;
};

const MetricBreakdownTable: React.FC<MetricBreakdownTableProps> = ({
  data,
  isLoading,
  shouldShowMetric,
  handleDownload,
}) => {
  const [expandedCampaigns, setExpandedCampaigns] = useState<Set<number>>(new Set());
  const [expandedLineItems, setExpandedLineItems] = useState<Set<string>>(new Set());
  const [isDownloading, setIsDownloading] = useState(false);

  // Process and aggregate data by campaign, line item, and creative
  const campaignMetrics: CampaignMetrics[] = React.useMemo(() => {
    const campaignsMap: { [key: number]: any } = {};

    data.forEach((row) => {
      // Initialize or update campaign
      if (!campaignsMap[row.campaign_id]) {
        campaignsMap[row.campaign_id] = {
          id: row.campaign_id,
          name: row.campaign_name,
          impressions: 0,
          clicks: 0,
          views: 0,
          spend: 0,
          conversions: 0,
          video_starts: 0,
          video_completes: 0,
          audio_starts: 0,
          audio_completes: 0,
          ctr: 0,
          cpa: 0,
          cpm: 0,
          video_completion_rate: 0,
          audio_completion_rate: 0,
          lineItems: {},
        };
      }

      const campaign = campaignsMap[row.campaign_id];
      campaign.impressions += row.impressions;
      campaign.clicks += row.clicks;
      campaign.views += row.views;
      campaign.spend += row.spend;
      campaign.conversions += row.conversions;
      campaign.video_starts += row.video_starts;
      campaign.video_completes += row.video_completes;
      campaign.audio_starts += row.audio_starts;
      campaign.audio_completes += row.audio_completes;

      // Update line item if it exists
      if (row.line_item_id && row.line_item_name) {
        if (!campaign.lineItems[row.line_item_id]) {
          campaign.lineItems[row.line_item_id] = {
            id: row.line_item_id,
            name: row.line_item_name ?? '',
            impressions: 0,
            clicks: 0,
            views: 0,
            spend: 0,
            conversions: 0,
            video_starts: 0,
            video_completes: 0,
            audio_starts: 0,
            audio_completes: 0,
            ctr: 0,
            cpa: 0,
            cpm: 0,
            video_completion_rate: 0,
            audio_completion_rate: 0,
            creatives: {},
          };
        }

        const lineItem = campaign.lineItems[row.line_item_id];
        lineItem.impressions += row.impressions;
        lineItem.clicks += row.clicks;
        lineItem.views += row.views;
        lineItem.spend += row.spend;
        lineItem.conversions += row.conversions;
        lineItem.video_starts += row.video_starts;
        lineItem.video_completes += row.video_completes;
        lineItem.audio_starts += row.audio_starts;
        lineItem.audio_completes += row.audio_completes;

        // Update creative if it exists
        if (row.creative_id && row.creative_name) {
          if (!lineItem.creatives[row.creative_id]) {
            lineItem.creatives[row.creative_id] = {
              id: row.creative_id,
              name: row.creative_name ?? '',
              impressions: 0,
              clicks: 0,
              views: 0,
              spend: 0,
              conversions: 0,
              video_starts: 0,
              video_completes: 0,
              audio_starts: 0,
              audio_completes: 0,
              ctr: 0,
              cpa: 0,
              cpm: 0,
              video_completion_rate: 0,
              audio_completion_rate: 0,
            };
          }

          const creative = lineItem.creatives[row.creative_id];
          creative.impressions += row.impressions;
          creative.clicks += row.clicks;
          creative.views += row.views;
          creative.spend += row.spend;
          creative.conversions += row.conversions;
          creative.video_starts += row.video_starts;
          creative.video_completes += row.video_completes;
          creative.audio_starts += row.audio_starts;
          creative.audio_completes += row.audio_completes;
        }
      }
    });

    // Calculate derived metrics and convert to final structure
    return Object.values(campaignsMap).map((campaign) => {
      // Calculate campaign-level metrics
      campaign.ctr = campaign.clicks / campaign.impressions;
      campaign.cpa = campaign.spend / campaign.conversions;
      campaign.cpm = (campaign.spend / campaign.impressions) * 1000;
      campaign.video_completion_rate = campaign.video_completes / campaign.video_starts;
      campaign.audio_completion_rate = campaign.audio_completes / campaign.audio_starts;

      // Process line items
      const lineItems = Object.values(campaign.lineItems).map((lineItem: any) => {
        // Calculate line item metrics
        lineItem.ctr = lineItem.clicks / lineItem.impressions;
        lineItem.cpa = lineItem.spend / lineItem.conversions;
        lineItem.cpm = (lineItem.spend / lineItem.impressions) * 1000;
        lineItem.video_completion_rate = lineItem.video_completes / lineItem.video_starts;
        lineItem.audio_completion_rate = lineItem.audio_completes / lineItem.audio_starts;

        // Process creatives
        const creatives = Object.values(lineItem.creatives).map((creative: any) => {
          creative.ctr = creative.clicks / creative.impressions;
          creative.cpa = creative.spend / creative.conversions;
          creative.cpm = (creative.spend / creative.impressions) * 1000;
          creative.video_completion_rate = creative.video_completes / creative.video_starts;
          creative.audio_completion_rate = creative.audio_completes / creative.audio_starts;
          return creative;
        });

        return {
          ...lineItem,
          creatives: creatives.length > 0 ? creatives : undefined,
        };
      });

      return {
        ...campaign,
        lineItems: lineItems.length > 0 ? lineItems : undefined,
      };
    });
  }, [data]);

  const toggleCampaign = (campaignId: number) => {
    setExpandedCampaigns(prev => {
      const next = new Set(prev);
      if (next.has(campaignId)) {
        next.delete(campaignId);
      } else {
        next.add(campaignId);
      }
      return next;
    });
  };

  const toggleLineItem = (campaignId: number, lineItemId: number | null) => {
    const key = `${campaignId}-${lineItemId}`;
    setExpandedLineItems(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const interactionTitle = () => {
    let clicks = 0;
    let views = 0;

    campaignMetrics.forEach(campaign => {
      clicks += campaign.clicks;
      views += campaign.views;
    });

    if (clicks > 0 && views > 0) {
      return 'Interactions';
    } else if (clicks > 0 && views === 0) {
      return 'Clicks';
    } else {
      return 'Views';
    }
  };

  const interactionValue = (campaign: CampaignMetrics) => {
    if (campaign.clicks > 0 && campaign.views > 0) {
      return campaign.clicks + campaign.views;
    } else if (campaign.clicks > 0 && campaign.views === 0) {
      return campaign.clicks;
    } else {
      return campaign.views;
    }
  };

  const interactionValueItem = (item: LineItemMetrics | CreativeMetrics) => {
    if (item.clicks > 0 && item.views > 0) {
      return item.clicks + item.views;
    } else if (item.clicks > 0 && item.views === 0) {
      return item.clicks;
    } else {
      return item.views;
    }
  };

  return (
    <div className="rounded-lg shadow-md bg-white flex-1 overflow-hidden">
      <div className="flex justify-between bg-gray-201">
        <div className="flex items-center justify-between w-full">
          <div className="flex gap-2 p-2 pl-4 items-center font-medium">
            <TrendingUpDown className="w-4 h-4" />
            Performance Breakdown
          </div>
          {/* <div className="border-l border-gray-401 h-1/2 ml-4 mr-2" /> */}
          <div className="flex items-center pr-2">
            <Button
              variant="ghost"
              size="med"
              disabled={isDownloading}
              onClick={async () => {
                setIsDownloading(true);
                try {
                  const result = handleDownload();
                  if (result && typeof (result as Promise<unknown>).then === 'function') {
                    await (result as Promise<void>);
                  }
                } finally {
                  setIsDownloading(false);
                }
              }}
            >
              {isDownloading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              {isDownloading ? 'Downloading...' : 'Download'}
            </Button>
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        {isLoading ? (
          <Skeleton className="w-full h-[200px]" />
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-400">
                <th className="text-left py-2 px-4 text-sm font-medium text-gray-600">
                  Campaign / Line Item / Creative
                </th>
                {shouldShowMetric('clicks') && (
                  <th className="text-center py-2 px-4 text-sm font-medium text-gray-600">
                    {interactionTitle()}
                  </th>
                )}
                {shouldShowMetric('impressions') && (
                  <th className="text-center py-2 px-4 text-sm font-medium text-gray-600">
                    Impressions
                  </th>
                )}
                {shouldShowMetric('conversions') && (
                  <th className="text-center py-2 px-4 text-sm font-medium text-gray-600">
                    Conv.
                  </th>
                )}
                {shouldShowMetric('ctr') && (
                  <th className="text-center py-2 px-4 text-sm font-medium text-gray-600">
                    CTR
                  </th>
                )}
                {shouldShowMetric('spend') && (
                  <th className="text-center py-2 px-4 text-sm font-medium text-gray-600">
                    Cost
                  </th>
                )}
                {shouldShowMetric('cpm') && (
                  <th className="text-center py-2 px-4 text-sm font-medium text-gray-600">
                    CPM
                  </th>
                )}
                {shouldShowMetric('cpa') && (
                  <th className="text-center py-2 px-4 text-sm font-medium text-gray-600">
                    CPA
                  </th>
                )}
                {shouldShowMetric('video_completion_rate') && (
                  <th className="text-center py-2 px-4 text-sm font-medium text-gray-600">
                    Video CR
                  </th>
                )}
                {shouldShowMetric('audio_completion_rate') && (
                  <th className="text-center py-2 px-4 text-sm font-medium text-gray-600">
                    Audio CR
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {campaignMetrics.map(campaign => (
                <React.Fragment key={campaign.id}>
                  <tr
                    className="border-b border-gray-200 hover:bg-gray-100 cursor-pointer"
                    onClick={() => toggleCampaign(campaign.id)}
                  >
                    <td className="py-2 px-4 font-medium flex items-center">
                      <span className="mr-2 text-gray-500 text-sm">
                        {expandedCampaigns.has(campaign.id) ? '▼' : '►'}
                      </span>
                      {campaign.name}
                    </td>
                    {shouldShowMetric('clicks') && (
                      <td className="text-right py-2 px-4">
                        {formatNumber(interactionValue(campaign))}
                      </td>
                    )}
                    {shouldShowMetric('impressions') && (
                      <td className="text-right py-2 px-4">
                        {formatNumber(campaign.impressions)}
                      </td>
                    )}
                    {shouldShowMetric('conversions') && (
                      <td className="text-right py-2 px-4">
                        {formatNumber(campaign.conversions)}
                      </td>
                    )}
                    {shouldShowMetric('ctr') && (
                      <td className="text-right py-2 px-4">
                        {formatPercentage(campaign.ctr)}
                      </td>
                    )}
                    {shouldShowMetric('spend') && (
                      <td className="text-right py-2 px-4">
                        {formatCurrency(campaign.spend)}
                      </td>
                    )}
                    {shouldShowMetric('cpm') && (
                      <td className="text-right py-2 px-4">
                        {formatCurrency(campaign.cpm)}
                      </td>
                    )}
                    {shouldShowMetric('cpa') && (
                      <td className="text-right py-2 px-4">
                        {formatCurrency(campaign.cpa)}
                      </td>
                    )}
                    {shouldShowMetric('video_completion_rate') && (
                      <td className="text-right py-2 px-4">
                        {formatPercentage(campaign.video_completion_rate)}
                      </td>
                    )}
                    {shouldShowMetric('audio_completion_rate') && (
                      <td className="text-right py-2 px-4">
                        {formatPercentage(campaign.audio_completion_rate)}
                      </td>
                    )}
                  </tr>
                  {expandedCampaigns.has(campaign.id) && campaign.lineItems?.map(lineItem => (
                    <React.Fragment key={lineItem.id}>
                      <tr
                        className="border-b border-gray-100 bg-gray-50 hover:bg-gray-100 cursor-pointer"
                        onClick={() => toggleLineItem(campaign.id, lineItem.id)}
                      >
                        <td className="py-2 px-4 pl-10 flex items-center">
                          <span className="mr-2 text-gray-500 text-sm">
                            {expandedLineItems.has(`${campaign.id}-${lineItem.id}`) ? '▼' : '►'}
                          </span>
                          {lineItem.name}
                        </td>
                        {shouldShowMetric('clicks') && (
                          <td className="text-right py-2 px-4">
                            {formatNumber(interactionValueItem(lineItem))}
                          </td>
                        )}
                        {shouldShowMetric('impressions') && (
                          <td className="text-right py-2 px-4">
                            {formatNumber(lineItem.impressions)}
                          </td>
                        )}
                        {shouldShowMetric('conversions') && (
                          <td className="text-right py-2 px-4">
                            {formatNumber(lineItem.conversions)}
                          </td>
                        )}
                        {shouldShowMetric('ctr') && (
                          <td className="text-right py-2 px-4">
                            {formatPercentage(lineItem.ctr)}
                          </td>
                        )}
                        {shouldShowMetric('spend') && (
                          <td className="text-right py-2 px-4">
                            {formatCurrency(lineItem.spend)}
                          </td>
                        )}
                        {shouldShowMetric('cpm') && (
                          <td className="text-right py-2 px-4">
                            {formatCurrency(lineItem.cpm)}
                          </td>
                        )}
                        {shouldShowMetric('cpa') && (
                          <td className="text-right py-2 px-4">
                            {formatCurrency(lineItem.cpa)}
                          </td>
                        )}
                        {shouldShowMetric('video_completion_rate') && (
                          <td className="text-right py-2 px-4">
                            {formatPercentage(lineItem.video_completion_rate)}
                          </td>
                        )}
                        {shouldShowMetric('audio_completion_rate') && (
                          <td className="text-right py-2 px-4">
                            {formatPercentage(lineItem.audio_completion_rate)}
                          </td>
                        )}
                      </tr>
                      {expandedLineItems.has(`${campaign.id}-${lineItem.id}`) && lineItem.creatives?.map(creative => (
                        <tr
                          key={creative.id}
                          className="border-b border-gray-100 bg-gray-50 hover:bg-gray-100"
                        >
                          <td className="py-2 px-4 pl-16">
                            {creative.name}
                          </td>
                          {shouldShowMetric('clicks') && (
                            <td className="text-right py-2 px-4">
                              {formatNumber(interactionValueItem(creative))}
                            </td>
                          )}
                          {shouldShowMetric('impressions') && (
                            <td className="text-right py-2 px-4">
                              {formatNumber(creative.impressions)}
                            </td>
                          )}
                          {shouldShowMetric('conversions') && (
                            <td className="text-right py-2 px-4">
                              {formatNumber(creative.conversions)}
                            </td>
                          )}
                          {shouldShowMetric('ctr') && (
                            <td className="text-right py-2 px-4">
                              {formatPercentage(creative.ctr)}
                            </td>
                          )}
                          {shouldShowMetric('spend') && (
                            <td className="text-right py-2 px-4">
                              {formatCurrency(creative.spend)}
                            </td>
                          )}
                          {shouldShowMetric('cpm') && (
                            <td className="text-right py-2 px-4">
                              {formatCurrency(creative.cpm)}
                            </td>
                          )}
                          {shouldShowMetric('cpa') && (
                            <td className="text-right py-2 px-4">
                              {formatCurrency(creative.cpa)}
                            </td>
                          )}
                          {shouldShowMetric('video_completion_rate') && (
                            <td className="text-right py-2 px-4">
                              {formatPercentage(creative.video_completion_rate)}
                            </td>
                          )}
                          {shouldShowMetric('audio_completion_rate') && (
                            <td className="text-right py-2 px-4">
                              {formatPercentage(creative.audio_completion_rate)}
                            </td>
                          )}
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default MetricBreakdownTable;